import { isTouchDevice } from '@theme/utilities';

/** Grace period (ms) before closing on pointerleave, to tolerate moving the cursor from a trigger to its panel. */
const CLOSE_DELAY_MS = 200;

/**
 * Desktop 3-level category flyout navigation.
 *
 * Opens on hover/focus for mouse & keyboard; on touch devices the first tap
 * on a trigger opens its panel instead of navigating (a second tap on the
 * same, now-open trigger navigates normally). The third-level sublist is
 * measured on open and flipped to the left when it would overflow the
 * viewport's right edge.
 */
class CategoryNav extends HTMLElement {
  /** @type {HTMLElement[]} */
  #triggers = [];

  /** @type {Map<HTMLElement, ReturnType<typeof setTimeout>>} */
  #closeTimers = new Map();

  connectedCallback() {
    this.#triggers = Array.from(this.querySelectorAll('[data-flyout-trigger]'));

    for (const trigger of this.#triggers) {
      trigger.addEventListener('pointerenter', (event) => this.#handlePointerEnter(event, trigger));
      trigger.addEventListener('pointerleave', (event) => this.#handlePointerLeave(event, trigger));
      trigger.addEventListener('focusin', () => this.#open(trigger));
      trigger.addEventListener('focusout', (event) => this.#handleFocusOut(event, trigger));

      const link = trigger.querySelector(':scope > a');
      link?.addEventListener('click', (event) => this.#handleClick(event, trigger));
    }

    document.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('pointerdown', this.#handleOutsideClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('pointerdown', this.#handleOutsideClick);
    for (const timer of this.#closeTimers.values()) clearTimeout(timer);
  }

  /** @param {PointerEvent} event @param {HTMLElement} trigger */
  #handlePointerEnter(event, trigger) {
    if (event.pointerType === 'touch') return;
    clearTimeout(this.#closeTimers.get(trigger));
    this.#open(trigger);
  }

  /** @param {PointerEvent} event @param {HTMLElement} trigger */
  #handlePointerLeave(event, trigger) {
    if (event.pointerType === 'touch') return;
    const timer = setTimeout(() => this.#close(trigger), CLOSE_DELAY_MS);
    this.#closeTimers.set(trigger, timer);
  }

  /** @param {FocusEvent} event @param {HTMLElement} trigger */
  #handleFocusOut(event, trigger) {
    const nextFocus = /** @type {Node | null} */ (event.relatedTarget);
    if (nextFocus && trigger.contains(nextFocus)) return;
    this.#close(trigger);
  }

  /** @param {MouseEvent} event @param {HTMLElement} trigger */
  #handleClick(event, trigger) {
    if (!isTouchDevice()) return;
    if (trigger.hasAttribute('data-open')) return;

    event.preventDefault();
    this.#open(trigger);
  }

  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Escape') return;
    for (const trigger of this.#triggers) this.#close(trigger);
  };

  #handleOutsideClick = (/** @type {PointerEvent} */ event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    for (const trigger of this.#triggers) this.#close(trigger);
  };

  /**
   * Opens a trigger's panel and, crucially, closes any sibling trigger at
   * the same level *immediately* (no delay) - this is what stops the old
   * third-level flyout from lingering and overlapping the new one when the
   * pointer moves directly from one second-level item to another. The
   * mouseleave delay only ever applies when the pointer leaves a trigger's
   * subtree without landing on a sibling or descendant.
   * @param {HTMLElement} trigger
   */
  #open(trigger) {
    clearTimeout(this.#closeTimers.get(trigger));
    this.#closeSiblings(trigger);
    trigger.setAttribute('data-open', '');
    trigger.querySelector(':scope > a[aria-haspopup]')?.setAttribute('aria-expanded', 'true');
    this.#positionSublist(trigger);
  }

  /** @param {HTMLElement} trigger */
  #close(trigger) {
    clearTimeout(this.#closeTimers.get(trigger));
    trigger.removeAttribute('data-open');
    trigger.removeAttribute('data-flip');
    trigger.querySelector(':scope > a[aria-haspopup]')?.setAttribute('aria-expanded', 'false');

    for (const nested of trigger.querySelectorAll('[data-flyout-trigger][data-open]')) {
      clearTimeout(this.#closeTimers.get(/** @type {HTMLElement} */ (nested)));
      nested.removeAttribute('data-open');
      nested.removeAttribute('data-flip');
      nested.querySelector(':scope > a[aria-haspopup]')?.setAttribute('aria-expanded', 'false');
    }
  }

  /** @param {HTMLElement} trigger */
  #closeSiblings(trigger) {
    const parent = trigger.parentElement;
    if (!parent) return;

    for (const sibling of parent.children) {
      if (sibling !== trigger && sibling.hasAttribute('data-flyout-trigger')) {
        this.#close(/** @type {HTMLElement} */ (sibling));
      }
    }
  }

  /**
   * Measures the level-3 sublist and flips it to the left of its trigger
   * when it would overflow the viewport's right edge.
   * @param {HTMLElement} trigger
   */
  #positionSublist(trigger) {
    const sublist = trigger.querySelector(':scope > .category-nav__sublist');
    if (!sublist) return;

    trigger.removeAttribute('data-flip');
    const rect = sublist.getBoundingClientRect();
    if (rect.right > document.documentElement.clientWidth) {
      trigger.setAttribute('data-flip', '');
    }
  }
}

if (!customElements.get('category-nav-component')) {
  customElements.define('category-nav-component', CategoryNav);
}
